import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { Loader2, CheckCircle2, XCircle, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, getToken } from "@/lib/api";
import { PC_MARK } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const token = params.get("token");
  const loggedIn = !!getToken();
  const [state, setState] = useState(token ? "verifying" : "resend");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        if (getToken()) await refreshUser();
        setState("success");
      } catch (e) {
        // If the account is already verified (e.g. duplicate click / reused link), treat as success.
        if (getToken()) {
          try {
            const { data } = await api.get("/auth/verification-status");
            if (data.email_verified) { await refreshUser(); setState("success"); return; }
          } catch {}
        }
        setState("error");
        setMsg(e?.response?.data?.detail || "This verification link is invalid or has expired.");
      }
    })();
  }, [token, refreshUser]);

  const resend = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", { email });
      setMsg("If your account requires verification, a new email has been sent.");
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Please try again in a few minutes.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4">
      <div className="max-w-md w-full text-center pc-reveal">
        <img src={PC_MARK} alt="PropertyConnect" className="h-10 w-10 object-contain mx-auto mb-4" />
        {state === "verifying" && (
          <div data-testid="verify-loading" className="flex flex-col items-center gap-3 text-slate-600">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
            <p>Verifying your email…</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="verify-success" className="flex flex-col items-center gap-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Email verified</h1>
            {loggedIn && user && user.account_type !== "CONSUMER" && user.verification_status !== "PROFESSIONAL_VERIFIED" ? (
              <>
                <p className="text-slate-500">Your email is confirmed. <span className="font-semibold text-slate-700">Next: get professionally verified</span> — it establishes your professional status and is required to publish listings to the Public Marketplace.</p>
                <div className="flex flex-col sm:flex-row gap-2.5 mt-2">
                  <Button data-testid="verify-get-verified" onClick={() => navigate("/profile")} className="h-11 bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl px-6">Get Professionally Verified</Button>
                  <Button data-testid="verify-explore" variant="outline" onClick={() => navigate("/")} className="h-11 rounded-xl px-6">Explore PropertyConnect</Button>
                </div>
              </>
            ) : loggedIn ? (
              <>
                <p className="text-slate-500">Your email is confirmed. Welcome to PropertyConnect.</p>
                <Button data-testid="verify-continue" onClick={() => navigate("/")} className="mt-2 h-11 bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl px-6">Continue to PropertyConnect</Button>
              </>
            ) : (
              <>
                <p className="text-slate-500">Your email is confirmed. Please sign in to continue.</p>
                <Link to="/login"><Button data-testid="verify-continue" className="mt-2 h-11 bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl px-6">Continue to sign in</Button></Link>
              </>
            )}
          </div>
        )}
        {(state === "error" || state === "resend") && (
          <div data-testid="verify-error" className="flex flex-col items-center gap-3">
            {state === "error" ? <XCircle className="h-12 w-12 text-red-500" /> : <MailCheck className="h-12 w-12 text-accent" />}
            <h1 className="font-display text-2xl font-bold text-slate-900">{state === "error" ? "Verification failed" : "Verify your email"}</h1>
            {msg && <p className="text-slate-500">{msg}</p>}
            <form onSubmit={resend} className="w-full mt-2 space-y-3">
              <Input data-testid="verify-resend-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your account email" className="h-11" />
              <Button data-testid="verify-resend-submit" type="submit" disabled={busy} className="w-full h-11 bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Resend verification email"}
              </Button>
            </form>
            <Link to="/login" className="text-sm text-accent font-semibold mt-1">Back to sign in</Link>
          </div>
        )}
      </div>
      <SiteFooter variant="minimal" />
    </div>
  );
}
